// @ts-nocheck
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
// import React from "react"; // Starter Custom Code
import styled from "styled-components";

/* Starter Custom Code - Start */
import { lazyLoadStyles } from "@store/Themes/common/mixins/lazyload";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface StyledImgWrapperProps extends InjectableCss {
    isLoading?: boolean;
    hideLoading?: boolean;
}

export const StyledImgWrapper = styled.span<StyledImgWrapperProps>`
    ${x => (!x.hideLoading && x.isLoading ? lazyLoadStyles() : null)}
    display: flex;
    ${injectCss}
`;
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import parseNumber from "@toolkit/Framework/Common/Utilities/parseNumber";
/* Store Custom Code - End */

// Store Custom Code - next line - export
export interface ImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<ImgProps>;
    // Starter Custom Code
    /** CSS string or styled-components function to be injected into the wrapper of the component.
     * @themable */
    wrapperCss?: StyledProp<StyledImgWrapperProps>;
    /** Hide the animation that occurs while the image is loading */
    hideLoading?: boolean;
    // Store Custom Code
    isRetina?: boolean;
}

export type ImgProps = MobiusStyledComponentProps<
    "img",
    ImagePresentationProps & {
        /** The URL to fetch the image from. If not provided, Img renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Controls whether the img is lazy or eager loaded, default is eager */
        loading?: string;
        // Event handler for screen readers accessing an image modal in ProductPrimaryImage.tsx
        onKeyPress?: React.KeyboardEventHandler<HTMLImageElement>;
    }
>;

function Img(props: ImgProps) {
    const { src, altText, loading, hideLoading, wrapperCss, isRetina, ...otherProps } = props; // Store Custom Code

    const dimensions = useQueryStringsDimensions(src);

    /* Store Custom Code - Start */
    const dimensionWidth = React.useMemo(() => {
        if (!isRetina || !dimensions.width) {
            return dimensions.width;
        }

        const parsedWidth = typeof dimensions.width === "string" ? parseNumber(dimensions.width) : dimensions.width;

        return parsedWidth ? Math.ceil(parsedWidth / 2) : dimensions.width;
    }, [isRetina, dimensions.width]);

    const dimensionHeight = React.useMemo(() => {
        if (!isRetina || !dimensions.height) {
            return dimensions.height;
        }

        const parsedHeight = typeof dimensions.height === "string" ? parseNumber(dimensions.height) : dimensions.height;

        return parsedHeight ? Math.ceil(parsedHeight / 2) : dimensions.height;
    }, [isRetina, dimensions.height]);
    /* Store Custom Code - End */

    /* Starter Custom Code - Start */
    const [isLoading, setIsLoading] = useState<boolean>();
    const imgRef = useRef<HTMLImageElement>(undefined);
    const stopLoading = useCallback(() => setIsLoading(false), []);

    useEffect(() => {
        if (!isLoading) {
            setIsLoading(imgRef.current && !imgRef.current.complete);
        }
    }, []);
    /* Starter Custom Code - End */

    if (!src) {
        return null;
    }

    return (
        <StyledImgWrapper /* Starter Custom Code */
            isLoading={isLoading}
            hideLoading={hideLoading}
            css={wrapperCss}
            // Store Custom Code
            className="image-wrapper"
        >
            <StyledImg
                {...otherProps}
                loading={loading || "lazy"}
                src={dimensions.url}
                alt={altText}
                width={dimensionWidth || otherProps.width} // Store Custom Code
                height={dimensionHeight || otherProps.height} // Store Custom Code
                // Starter Custom Code
                ref={imgRef}
                onLoad={stopLoading}
                onError={stopLoading}
            />
        </StyledImgWrapper>
    );
}

const useQueryStringsDimensions = (src?: string) => {
    return React.useMemo(() => {
        const queryStrings = src && /\?.*/.exec(src);
        const noDimensions = {
            url: src,
            width: "",
            height: "",
        };

        if (queryStrings) {
            const parsedUrl = parseImageUrl(src);

            if (!parsedUrl.width || !parsedUrl.height) {
                return noDimensions;
            }
            return parsedUrl;
        }
        return noDimensions;
    }, [src]);
};

const StyledImg = styled.img<InjectableCss>`
    ${injectCss}
`;

export default Img;
